import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { MapPin, Users, Star } from "lucide-react";
import { VenueContactSidebar } from "./VenueContactSidebar";

interface VenueSidebarProps {
  venue: any;
  slug: string;
  avgRating: number | null;
  reviewCount: number;
  estimatedPrice: number | null;
  priceReportsCount: number;
  isLoggedIn: boolean;
}

function HalfStarRow({ value }: { value: number }) {
  return (
    <div className="flex items-center gap-0.5">
      {[1, 2, 3, 4, 5].map((n) => {
        const filled = value >= n;
        const halfFilled = !filled && value >= n - 0.5;
        return (
          <div key={n} className="relative">
            <Star className="h-4 w-4 text-muted-foreground/25" />
            {(filled || halfFilled) && (
              <div
                className="absolute inset-0 overflow-hidden"
                style={{ width: halfFilled ? "50%" : "100%" }}
              >
                <Star className="h-4 w-4 fill-warning text-warning" />
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

export function VenueSidebar({
  venue,
  slug,
  avgRating,
  reviewCount,
  estimatedPrice,
  priceReportsCount,
  isLoggedIn,
}: VenueSidebarProps) {
  const officialPrice = venue.official_price;

  return (
    <div className="space-y-5">
      {/* Summary card */}
      <div className="rounded-lg border p-5 space-y-4">
        {/* Name */}
        <h1 className="font-display text-2xl font-bold leading-tight">
          {venue.name}
        </h1>

        {/* Location & capacity */}
        <div className="flex flex-col gap-1 text-sm text-muted-foreground">
          <span className="flex items-center gap-1.5">
            <MapPin className="h-3.5 w-3.5 shrink-0" />
            {venue.city}
            {venue.autonomous_community && `, ${venue.autonomous_community}`}
          </span>
          <span className="flex items-center gap-1.5">
            <Users className="h-3.5 w-3.5 shrink-0" />
            {venue.capacity ? `${venue.capacity} personas` : "Aforo no disponible"}
          </span>
        </div>

        {/* Rating */}
        {avgRating != null && (
          <div className="flex items-center gap-2">
            <HalfStarRow value={avgRating} />
            <span className="font-display text-base font-bold">
              {avgRating.toFixed(1)}
            </span>
            <span className="text-xs text-muted-foreground">
              ({reviewCount} review{reviewCount !== 1 ? "s" : ""})
            </span>
          </div>
        )}

        {/* Pricing */}
        <div>
          <h3 className="mb-2 text-xs font-semibold uppercase tracking-widest text-muted-foreground">
            Precio
          </h3>
          <div className="grid grid-cols-2 gap-3">
            {/* Official first */}
            <div className="rounded-lg border p-3">
              <p className="text-[11px] text-muted-foreground mb-1">Oficial</p>
              <p className="font-display text-xl font-bold">
                {officialPrice != null ? `${officialPrice}€` : "—"}
              </p>
              {venue.official_price_notes && (
                <p className="text-[10px] text-muted-foreground mt-0.5 leading-snug">
                  {venue.official_price_notes}
                </p>
              )}
            </div>
            {/* Community second */}
            <div className="rounded-lg border p-3">
              <p className="text-[11px] text-muted-foreground mb-1">Comunidad</p>
              <p className="font-display text-xl font-bold">
                {estimatedPrice != null && !isNaN(estimatedPrice)
                  ? `${Math.round(estimatedPrice)}€`
                  : "—"}
              </p>
              {priceReportsCount > 0 && (
                <p className="text-[10px] text-muted-foreground mt-0.5">
                  {priceReportsCount} contribucion{priceReportsCount !== 1 ? "es" : ""}
                </p>
              )}
            </div>
          </div>
        </div>

        {/* CTA */}
        {isLoggedIn ? (
          <Button asChild size="lg" className="w-full">
            <Link to={`/sala/${slug}/review`}>Escribe tu review</Link>
          </Button>
        ) : (
          <Button asChild size="lg" className="w-full">
            <Link to="/auth">Inicia sesión para dejar una review</Link>
          </Button>
        )}

        {/* Consultar agenda CTA */}
        {(() => {
          const raw = venue.consultar_agenda_url || venue.website;
          if (!raw) return null;
          let url = raw.trim();
          if (!/^https?:\/\//i.test(url)) url = `https://${url}`;
          try { new URL(url); } catch { return null; }
          return (
            <a
              href={url}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center justify-center w-full h-11 rounded-md px-8 text-sm font-medium border-2 border-foreground text-foreground bg-background hover:bg-foreground hover:text-background transition-colors"
            >
              Consultar agenda
            </a>
          );
        })()}
      </div>

      {/* Contact card — separate */}
      <div className="rounded-lg border p-5">
        <VenueContactSidebar venue={venue} />
      </div>
    </div>
  );
}
